export default function MarketInsightsSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            El Paso Rental Property Market Insights
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Understanding the local market helps you make informed decisions about selling your tenant occupied property.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="relative">
            <img 
              src="https://public.readdy.ai/ai/img_res/e858028a3a0c4bfe57a8818c333536e5.jpg"
              alt="El Paso Rental Property Market"
              className="w-full h-auto rounded-lg shadow-2xl object-cover"
            />
          </div>

          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Current Market Conditions</h3>
            <div className="space-y-6">
              <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg">
                <div className="flex items-center mb-2">
                  <i className="ri-home-4-fill text-teal-600 text-2xl mr-3"></i>
                  <h4 className="font-bold text-gray-900 text-lg">Strong Rental Demand</h4>
                </div>
                <p className="text-gray-700">
                  El Paso's rental market remains strong with occupancy rates averaging 94-96%. This makes tenant occupied properties attractive investments for buyers like us.
                </p>
              </div>

              <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg">
                <div className="flex items-center mb-2">
                  <i className="ri-line-chart-fill text-teal-600 text-2xl mr-3"></i>
                  <h4 className="font-bold text-gray-900 text-lg">Rising Property Values</h4>
                </div>
                <p className="text-gray-700">
                  Rental property values in El Paso have appreciated steadily. Now may be an excellent time to cash out your investment and realize your gains.
                </p>
              </div>

              <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg">
                <div className="flex items-center mb-2">
                  <i className="ri-funds-fill text-teal-600 text-2xl mr-3"></i>
                  <h4 className="font-bold text-gray-900 text-lg">Investor Competition</h4>
                </div>
                <p className="text-gray-700">
                  Institutional and individual investors are actively seeking rental properties in El Paso. This competition helps maintain strong prices for sellers.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-12 text-white">
          <h3 className="text-3xl font-bold mb-8 text-center">Why Landlords Are Selling Now</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-exchange-dollar-fill text-white text-3xl"></i>
              </div>
              <h4 className="font-bold text-xl mb-3">1031 Exchanges</h4>
              <p className="text-gray-300">
                Many landlords are selling tenant occupied properties to exchange into larger or better-located investments.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-heart-fill text-white text-3xl"></i>
              </div>
              <h4 className="font-bold text-xl mb-3">Retirement</h4>
              <p className="text-gray-300">
                After years of landlording, many owners are ready to retire and enjoy life without tenant calls and property management.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-fill text-white text-3xl"></i>
              </div>
              <h4 className="font-bold text-xl mb-3">Relocation</h4>
              <p className="text-gray-300">
                Job transfers and life changes make managing El Paso rental properties from a distance impractical.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-emotion-unhappy-fill text-white text-3xl"></i>
              </div>
              <h4 className="font-bold text-xl mb-3">Tenant Problems</h4>
              <p className="text-gray-300">
                Difficult tenants, non-payment, and constant maintenance issues drive many landlords to sell.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-gear-fill text-white text-3xl"></i>
              </div>
              <h4 className="font-bold text-xl mb-3">Deferred Maintenance</h4>
              <p className="text-gray-300">
                Properties needing significant repairs are difficult to manage with tenants in place. Selling as-is is often the best solution.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-fill text-white text-3xl"></i>
              </div>
              <h4 className="font-bold text-xl mb-3">Cash Out Equity</h4>
              <p className="text-gray-300">
                With strong appreciation, many landlords are cashing out their equity to pursue other opportunities or investments.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-xl shadow-lg text-center">
            <div className="text-4xl font-bold text-teal-600 mb-2">$1,200</div>
            <div className="text-gray-600 font-semibold mb-3">Average Rent (2BR)</div>
            <p className="text-gray-600 text-sm">
              El Paso's affordable rents keep occupancy high and make rental properties attractive investments.
            </p>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-xl shadow-lg text-center">
            <div className="text-4xl font-bold text-teal-600 mb-2">7-9%</div>
            <div className="text-gray-600 font-semibold mb-3">Cap Rates</div>
            <p className="text-gray-600 text-sm">
              Strong cap rates make El Paso rental properties appealing to investors seeking cash flow.
            </p>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-xl shadow-lg text-center">
            <div className="text-4xl font-bold text-teal-600 mb-2">95%</div>
            <div className="text-gray-600 font-semibold mb-3">Occupancy Rate</div>
            <p className="text-gray-600 text-sm">
              High occupancy rates mean tenant occupied properties maintain consistent rental income.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}